import { useEffect, useMemo, useState } from "react";
import {
    X,
    Check,
    ArrowRight,
    MessageSquare,
    Repeat,
    Clock,
    SkipForward,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PlateBreakdown from "./PlateBreakdown";
import WheelPicker from "./WheelPicker";
import { roundTo5 } from "@/lib/plates";

function formatHMS(totalSec) {
    const s = Math.max(0, Math.floor(totalSec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const mm = String(m).padStart(h > 0 ? 2 : 1, "0");
    const ss = String(sec).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function formatMSS(totalSec) {
    const s = Math.max(0, Math.floor(totalSec));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Fullscreen one-set-at-a-time runner.
 * Phase = 'running' | 'wheel' | 'done'
 *
 * Phase 2 (2026-02-26): driven by a flattened `step` from
 * `lib/workoutSteps.buildSteps`. Each step resolves to:
 *   { block, slot, level, set, slotLabel, round, isLinkedBlock, ... }
 * so the overlay no longer has to traverse the workout tree.
 */
export default function FocusModeOverlay({
    workout,
    step,
    steps = [],
    stepIdx = 0,
    actuals,
    setActuals,
    phase,
    pendingDelta,
    setPendingDelta,
    maxes,
    equipmentMap,
    exerciseMap,
    onClose,
    onSetComplete,
    onSkipRest,
    onConfirmWheel,
    onSaveMax,
    typedMaxes = {},
    onSaveTypedMax,
    onChangeLevel,
    sessionStats,
    startedAt,
}) {
    const block = step?.block;
    const level = step?.level;
    const set = step?.set;
    // Per-block context: which steps belong to this block, and which one are we on?
    const blockSteps = useMemo(
        () => steps.filter((s) => s.blockIdx === step?.blockIdx),
        [steps, step?.blockIdx],
    );
    const stepInBlock = step?.stepInBlockIdx ?? 0;
    const totalSetsInBlock = step?.totalSetsInBlock ?? blockSteps.length;
    const isLinked = !!step?.isLinkedBlock;
    const linkLabel = step?.linkType === "circuit" ? "Circuit" : step?.linkType === "superset" ? "Superset" : null;
    // Stable big title for linked blocks — "Goblet Squat + Push-ups" — so the
    // heading doesn't swap on every step (Sachin 2026-06: "all exercise text
    // changes" was disorienting mid-superset).
    const linkedTitle = useMemo(() => {
        if (!isLinked) return "";
        const seen = new Map();
        blockSteps.forEach((s) => {
            if (!seen.has(s.slotIdx)) seen.set(s.slotIdx, s.level?.exercise_name || "");
        });
        return Array.from(seen.values()).filter(Boolean).join(" + ");
    }, [isLinked, blockSteps]);
    // Linked blocks render grouped by round: "SET 1" (all exercises), then
    // "SET 2" below. Each entry keeps its ORIGINAL blockSteps index so the
    // done/current/upcoming math stays untouched.
    const roundGroups = useMemo(() => {
        if (!isLinked) return [];
        const map = new Map();
        blockSteps.forEach((s, i) => {
            if (!map.has(s.round)) map.set(s.round, []);
            map.get(s.round).push({ s, i });
        });
        return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
    }, [isLinked, blockSteps]);

    const [actualWeight, setActualWeight] = useState("");
    // Inline max-entry: when the athlete reaches an exercise they haven't set
    // a max for yet, they type it right here. We auto-save on "Set Complete"
    // so they never have to tap an extra button. Per-exercise (NOT per-set).
    const [pendingMaxValue, setPendingMaxValue] = useState("");
    const [savingMax, setSavingMax] = useState(false);
    // Rest countdown: when the user taps ✓ on a set with rest_mins > 0, we
    // disable the NEXT set's ✓ until the countdown expires (or the user
    // taps "Skip rest"). Stored as a timestamp so a 1Hz tick re-renders
    // without needing per-second setState chains.
    const [restEndsAt, setRestEndsAt] = useState(0);
    // 1Hz tick — also drives the overall workout elapsed counter in the
    // top bar so the athlete can see total time at a glance.
    const [tickNow, setTickNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setTickNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const restRemainingSec = Math.max(0, Math.ceil((restEndsAt - tickNow) / 1000));
    const isResting = restRemainingSec > 0;

    // Lookup which exercise's max drives this set's percent calc.
    const driverExerciseId = level?.based_off_exercise_id || level?.exercise_id;
    const savedMax = maxes?.[driverExerciseId]?.value || 0;
    // EFFECTIVE max for live recalculation: prefer the in-progress pending
    // value if the athlete is editing the max inline next to the exercise
    // name. Lets prescribed weights tick as they type.
    const pendingMaxNum = Number(pendingMaxValue);
    const effectiveMax = (pendingMaxValue !== "" && pendingMaxNum > 0) ? pendingMaxNum : savedMax;
    const setPct = set ? parseFloat(set.percent_max) : NaN;
    // Block ✓ only when (a) the set uses percent_max AND (b) no max is
    // available at all (neither saved nor pending). User just needs to
    // type a number in the inline field to unblock.
    const needsMaxNow =
        phase === "running"
        && !!set
        && Number.isFinite(setPct)
        && setPct > 0
        && !effectiveMax
        && !!onSaveMax;
    // True when the pending value is set AND differs from what's saved —
    // means we should persist on next ✓.
    const pendingMaxDiffers =
        pendingMaxValue !== ""
        && pendingMaxNum > 0
        && pendingMaxNum !== savedMax
        && !!onSaveMax;
    const prescribed = useMemo(() => {
        if (!set || !effectiveMax) return 0;
        const pct = parseFloat(set.percent_max);
        if (Number.isNaN(pct)) return 0;
        return roundTo5((pct / 100) * effectiveMax);
    }, [set, effectiveMax]);

    const exercise = exerciseMap?.[level?.exercise_id];
    const equipment = exercise?.bar_equipment_id ? equipmentMap?.[exercise.bar_equipment_id] : null;

    // Reset transient inputs when the step or phase changes.
    useEffect(() => {
        setActualWeight("");
    }, [stepIdx, phase]);
    useEffect(() => {
        // Clear the max input when we move to a new driver exercise.
        setPendingMaxValue("");
    }, [driverExerciseId]);
    // Drop any stale rest countdown when we leave the running phase
    // (e.g. wheel pops between blocks, or workout finishes).
    useEffect(() => {
        if (phase !== "running") setRestEndsAt(0);
    }, [phase]);

    if (!block || !level || (phase !== "wheel" && !set)) return null;

    const persistPendingMaxIfNeeded = async () => {
        // Save when: athlete typed a max for an exercise with no saved value
        // OR they edited an existing max to something different (re-test day).
        if (!needsMaxNow && !pendingMaxDiffers) return true;
        const num = Number(pendingMaxValue);
        if (!num || num <= 0) {
            // No value — block Set Complete; user must type a max first.
            return false;
        }
        setSavingMax(true);
        try {
            await onSaveMax({ [driverExerciseId]: num });
            return true;
        } catch (err) {
            // Surface but don't crash; let user retry.
            console.error("Couldn't save max", err);
            return false;
        } finally {
            setSavingMax(false);
        }
    };

    const handleSetComplete = async () => {
        const ok = await persistPendingMaxIfNeeded();
        if (!ok) return;
        // Start the rest countdown for the NEXT set based on THIS set's
        // rest_mins. The next row's ✓ will be disabled while restRemainingSec
        // > 0; user can also hit "Skip rest" to clear it immediately.
        // Only start if there IS a next set in the same block — otherwise
        // we're transitioning to the wheel/next block and don't want a
        // stale countdown lingering.
        const restSec = parseFloat(set.rest_mins) * 60 || 0;
        const isLastInBlock = stepInBlock + 1 >= totalSetsInBlock;
        if (restSec > 0 && !isLastInBlock) {
            setRestEndsAt(Date.now() + restSec * 1000);
        } else {
            setRestEndsAt(0);
        }
        onSetComplete(actualWeight ? Number(actualWeight) : prescribed);
    };

    const skipRest = () => setRestEndsAt(0);

    return (
        <div
            data-testid="focus-mode-overlay"
            className="fixed inset-0 z-50 bg-brand-obsidian flex flex-col"
        >
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-900">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red flex items-center gap-2">
                        Focus mode · {block.name}
                        {isLinked && (
                            <span className="inline-flex items-center gap-1 border border-emerald-600/60 bg-emerald-900/30 text-emerald-200 px-1.5 py-0.5 text-[9px] tracking-[0.2em]">
                                <Repeat className="w-2.5 h-2.5" />
                                {linkLabel}
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-0.5">
                        {workout.title}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {startedAt && phase !== "done" && (
                        <div
                            data-testid="workout-elapsed"
                            title="Total workout time"
                            className="hidden sm:flex items-center gap-1.5 border border-zinc-800 bg-black/60 px-2.5 py-1.5 font-mono text-sm tracking-wider text-white"
                        >
                            <Clock className="w-3.5 h-3.5 text-brand-red" />
                            {formatHMS(Math.max(0, Math.floor((tickNow - new Date(startedAt).getTime()) / 1000)))}
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid="focus-close"
                        onClick={onClose}
                        className="inline-flex items-center justify-center w-9 h-9 border border-zinc-800 text-zinc-200 hover:text-brand-red"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {phase === "wheel" ? (
                    <motion.div
                        key="wheel"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.25 }}
                        className="flex-1 flex flex-col items-center justify-center px-6"
                        data-testid="wheel-step"
                    >
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3">
                            {level.exercise_name}
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white text-center uppercase mb-2">
                            Next time, I want to:
                        </h2>
                        <p className="text-sm text-zinc-200 text-center max-w-md mb-8">
                            Bump or pull back the load. We&apos;ll auto-tune your 1RM by the amount you pick. 5 lb steps only.
                        </p>
                        <WheelPicker value={pendingDelta} onChange={setPendingDelta} />
                        <button
                            type="button"
                            data-testid="wheel-confirm"
                            onClick={onConfirmWheel}
                            className="btn-tactical mt-8 inline-flex"
                        >
                            <Check className="w-4 h-4" /> Confirm
                        </button>
                    </motion.div>
                ) : phase === "done" ? (
                    <motion.div
                        key="done"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex-1 flex flex-col items-center justify-center px-6"
                        data-testid="focus-done"
                    >
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-red text-white mb-6">
                            <Check className="w-10 h-10" />
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl tracking-wider text-white text-center uppercase">
                            Workout complete.
                        </h2>
                        <p className="text-sm text-zinc-200 mt-3 text-center">
                            {sessionStats.setsCompleted} sets · {Math.round(sessionStats.elapsedSec / 60)} min
                        </p>
                        <button
                            type="button"
                            data-testid="focus-done-close"
                            onClick={onClose}
                            className="btn-tactical mt-8 inline-flex"
                        >
                            <ArrowRight className="w-4 h-4" /> Back to workout
                        </button>
                    </motion.div>
                ) : (
                    <motion.div
                        key={`block-${step.blockIdx}`}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.2 }}
                        className="flex-1 flex flex-col overflow-y-auto"
                    >
                        <div className="flex-1 flex flex-col px-5 md:px-8 py-5 pb-10 max-w-3xl w-full mx-auto">
                            {/* Block header */}
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2 flex items-center gap-2 flex-wrap">
                                <span>{step.slotLabel}</span>
                                {isLinked ? (
                                    <span>· Set {step.round} of {step.totalRounds}</span>
                                ) : (
                                    <span>· Set {stepInBlock + 1} of {totalSetsInBlock}</span>
                                )}
                                <span className="text-zinc-500">· {sessionStats.setsCompleted}/{steps.length} done</span>
                            </div>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white leading-[0.95] uppercase flex-1 min-w-0">
                                    {isLinked ? linkedTitle : level.exercise_name}
                                </h2>
                                {/* Live max chip — sits next to the exercise
                                    name. Typing here recomputes prescribed
                                    weights on every set row in real time. */}
                                {onSaveMax && Number.isFinite(setPct) && setPct > 0 && (
                                    <div
                                        data-testid="inline-max-chip"
                                        className={`shrink-0 inline-flex items-center gap-2 border px-3 py-2 ${
                                            needsMaxNow
                                                ? "border-amber-500 bg-amber-500/10"
                                                : pendingMaxDiffers
                                                    ? "border-sky-500 bg-sky-500/10"
                                                    : "border-zinc-800 bg-black/60"
                                        }`}
                                    >
                                        <span className={`text-[9px] tracking-[0.25em] uppercase ${
                                            needsMaxNow ? "text-amber-300" : pendingMaxDiffers ? "text-sky-300" : "text-zinc-400"
                                        }`}>
                                            Max
                                        </span>
                                        <input
                                            id="inline-max-input"
                                            type="number"
                                            min="0"
                                            step="5"
                                            inputMode="numeric"
                                            placeholder={savedMax ? String(savedMax) : "—"}
                                            value={pendingMaxValue}
                                            onChange={(e) => setPendingMaxValue(e.target.value)}
                                            data-testid="inline-max-input"
                                            className="w-16 bg-transparent text-center font-display text-xl tracking-wider text-white focus:outline-none"
                                        />
                                        <span className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">lbs</span>
                                    </div>
                                )}
                            </div>
                            {/* Typed maxes panel (2026-06, Sachin: "maxes aren't
                                showing up on speed stuff… some have 2 maxes").
                                Any exercise with declared max types (time, mph,
                                height…) or saved max rows shows ALL of them here,
                                editable mid-workout — even without percent_max. */}
                            {(() => {
                                if (!onSaveTypedMax || !level) return null;
                                const percentDriven = Number.isFinite(setPct) && setPct > 0;
                                if (percentDriven) return null; // driver chip above handles it
                                const exDoc = exerciseMap?.[level.exercise_id];
                                const cfg = exDoc?.maxes_config || [];
                                const savedRows = typedMaxes?.[level.exercise_id] || [];
                                const explicitCfg = cfg.length >= 2
                                    || (cfg.length === 1 && (cfg[0]?.type !== "weight_lbs" || cfg[0]?.label));
                                if (!savedRows.length && !explicitCfg) return null;
                                const entries = [];
                                const seen = new Set();
                                cfg.forEach((c) => {
                                    if (c?.type && !seen.has(c.type)) {
                                        seen.add(c.type);
                                        const row = savedRows.find((r) => (r.max_type || "1RM") === c.type);
                                        entries.push({ type: c.type, label: c.label, value: row?.max_weight || 0 });
                                    }
                                });
                                savedRows.forEach((r) => {
                                    const t = r.max_type || "1RM";
                                    if (!seen.has(t)) {
                                        seen.add(t);
                                        entries.push({ type: t, label: null, value: r.max_weight || 0 });
                                    }
                                });
                                if (!entries.length) return null;
                                return (
                                    <div data-testid="typed-max-panel" className="mt-3 flex flex-wrap gap-2">
                                        {entries.map((en) => (
                                            <TypedMaxInput
                                                key={en.type}
                                                entry={en}
                                                onSave={(num) => onSaveTypedMax(
                                                    level.exercise_id,
                                                    level.exercise_name || "",
                                                    en.type,
                                                    num,
                                                )}
                                            />
                                        ))}
                                    </div>
                                );
                            })()}
                            {isLinked && (
                                <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-emerald-300">
                                    {linkLabel} · finish every exercise in the set, then move to the next set
                                </div>
                            )}
                            {/* Level progression chip — only renders when the
                                current slot has more than one level defined.
                                Persists across workouts via /me/exercise-progression
                                so once Riley advances to Level 3 on Box Squat,
                                every workout that includes Box Squat opens at
                                Level 3 next time. */}
                            {onChangeLevel && (step.slot?.levels?.length ?? 0) > 1 && (
                                <div
                                    data-testid="level-progression-chip"
                                    className="mt-3 inline-flex items-center gap-2 border border-zinc-800 bg-zinc-950/60 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase"
                                >
                                    <span className="text-zinc-400">Level</span>
                                    {step.slot.levels.map((lv, i) => {
                                        const isCurrent = lv.exercise_id === level.exercise_id;
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                data-testid={`level-${i + 1}`}
                                                onClick={() => onChangeLevel(i)}
                                                title={lv.exercise_name || `Level ${i + 1}`}
                                                className={`min-w-[1.75rem] px-1.5 py-0.5 border ${
                                                    isCurrent
                                                        ? "border-sky-400 bg-sky-500/15 text-sky-300"
                                                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                                                }`}
                                            >
                                                {i + 1}
                                            </button>
                                        );
                                    })}
                                    <span className="text-zinc-500 normal-case tracking-normal ml-1">
                                        Saves to every workout
                                    </span>
                                </div>
                            )}
                            {needsMaxNow && (
                                <div
                                    data-testid="max-needed-hint"
                                    className="mt-2 text-[10px] tracking-[0.2em] uppercase text-amber-300"
                                >
                                    Tap the Max field to unlock prescribed weights · saves on first ✓
                                </div>
                            )}
                            {!needsMaxNow && pendingMaxDiffers && (
                                <div
                                    data-testid="max-edited-hint"
                                    className="mt-2 text-[10px] tracking-[0.2em] uppercase text-sky-300"
                                >
                                    Live preview · saves when you tap ✓
                                </div>
                            )}

                            {block.coach_note && (
                                <div className="mt-4 flex items-start gap-2 border border-zinc-900 bg-black/40 px-4 py-2 text-xs text-zinc-300">
                                    <MessageSquare className="w-3.5 h-3.5 text-brand-red flex-shrink-0 mt-0.5" />
                                    <span>{block.coach_note}</span>
                                </div>
                            )}

                            {/* All sets in this block — current highlighted, done checked, upcoming dim.
                                Linked (superset/circuit) blocks group by round: "SET 1" with every
                                exercise stacked, then "SET 2" below (Sachin 2026-06). */}
                            <div className="mt-6 space-y-3">
                                {(isLinked ? roundGroups : [[null, blockSteps.map((s, i) => ({ s, i }))]]).map(([round, entries]) => (
                                    <div key={round === null ? "flat" : `set-${round}`}>
                                        {isLinked && (
                                            <div
                                                data-testid={`runner-set-header-${round}`}
                                                className={`mb-2 mt-4 first:mt-0 text-[11px] tracking-[0.3em] uppercase font-semibold ${
                                                    round === step.round ? "text-brand-red" : entries.every(({ i }) => i < stepInBlock) ? "text-emerald-400" : "text-zinc-500"
                                                }`}
                                            >
                                                Set {round} of {step.totalRounds}
                                            </div>
                                        )}
                                        <div className="space-y-3">
                                            {entries.map(({ s: bs, i }) => {
                                                const done = i < stepInBlock;
                                                const current = i === stepInBlock;
                                                const upcoming = i > stepInBlock;
                                                return (
                                                    <SetRow
                                                        key={`${bs.blockIdx}-${bs.slotIdx}-${bs.setIdx}`}
                                                        step={bs}
                                                        indexInBlock={i}
                                                        totalInBlock={totalSetsInBlock}
                                                        isLinked={isLinked}
                                                        done={done}
                                                        current={current}
                                                        upcoming={upcoming}
                                                        driverMax={effectiveMax}
                                                        actualWeight={current ? actualWeight : undefined}
                                                        setActualWeight={current ? setActualWeight : undefined}
                                                        onComplete={current ? handleSetComplete : undefined}
                                                        isResting={current && isResting}
                                                        restRemainingSec={current ? restRemainingSec : 0}
                                                        onSkipRest={current ? skipRest : undefined}
                                                        disableCheck={
                                                            current && (
                                                                isResting
                                                                || savingMax
                                                                || (needsMaxNow && (!pendingMaxValue || Number(pendingMaxValue) <= 0))
                                                            )
                                                        }
                                                        savingMax={current && savingMax}
                                                        equipment={equipment}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}


/**
 * One row in the block list. Renders all sets of the current block;
 * highlights the active one, shows green check on completed ones, dims
 * the upcoming ones. The active row exposes an Actual override + a small
 * round Check button on the right that advances to the next set.
 */
function SetRow({
    step,
    indexInBlock,
    totalInBlock,
    isLinked,
    done,
    current,
    upcoming,
    driverMax,
    actualWeight,
    setActualWeight,
    onComplete,
    disableCheck,
    savingMax,
    equipment,
    isResting,
    restRemainingSec,
    onSkipRest,
}) {
    const set = step.set;
    const level = step.level;
    const pct = parseFloat(set.percent_max);
    const prescribed = useMemo(() => {
        if (!driverMax || Number.isNaN(pct)) return 0;
        return roundTo5((pct / 100) * driverMax);
    }, [driverMax, pct]);
    const restMin = parseFloat(set.rest_mins) || 0;

    const stateClass = current
        ? "border-sky-500 bg-sky-950/30 shadow-[0_0_18px_rgba(56,189,248,0.18)]"
        : done
            ? "border-emerald-900/70 bg-emerald-950/15 opacity-80"
            : "border-zinc-900 bg-zinc-950/40 opacity-50";

    const labelTone = current ? "text-sky-300" : done ? "text-emerald-300" : "text-zinc-400";
    const weightTone = current ? "text-white" : done ? "text-zinc-300" : "text-zinc-500";

    const setLabel = isLinked ? step.slotLabel : "SET";
    const setNumDisplay = isLinked ? "" : `${indexInBlock + 1}`;

    return (
        <div
            data-testid={`set-row-${indexInBlock}${current ? "-current" : done ? "-done" : ""}`}
            className={`border ${stateClass} px-4 py-3 transition-colors`}
        >
            <div className="flex items-center gap-3">
                <div className="shrink-0 min-w-[3.5rem]">
                    <div className={`text-[10px] tracking-[0.25em] uppercase ${labelTone}`}>
                        {setLabel} {setNumDisplay}
                    </div>
                    {!isLinked && (
                        <div className={`text-[10px] tracking-[0.15em] uppercase ${current ? "text-zinc-400" : "text-zinc-500"}`}>
                            of {totalInBlock}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    {isLinked && (
                        <div className={`font-display text-lg tracking-wider uppercase truncate ${current ? "text-white" : done ? "text-zinc-300" : "text-zinc-500"}`}>
                            {level.exercise_name}
                        </div>
                    )}
                    <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`font-display text-3xl tracking-wider ${weightTone}`}>
                        {prescribed || "—"}
                    </span>
                    <span className={`text-[10px] tracking-[0.2em] uppercase ${current ? "text-zinc-400" : "text-zinc-500"}`}>lbs</span>
                    <span className={`text-[10px] tracking-[0.2em] uppercase ${current ? "text-zinc-500" : "text-zinc-600"}`}>×</span>
                    <span className={`font-display text-2xl tracking-wider ${current ? "text-sky-300" : done ? "text-emerald-300" : "text-zinc-500"}`}>
                        {set.reps}
                    </span>
                    <span className={`text-[10px] tracking-[0.2em] uppercase ${current ? "text-zinc-400" : "text-zinc-500"}`}>reps</span>
                    {restMin > 0 && (
                        <span className={`ml-2 text-[10px] tracking-[0.15em] uppercase ${current ? "text-zinc-400" : "text-zinc-500"}`}>
                            · {restMin < 1 ? `${Math.round(restMin * 60)}s` : `${restMin} min`} rest
                        </span>
                    )}
                    </div>
                </div>
                {current && isResting ? (
                    <button
                        type="button"
                        data-testid={`skip-rest-${indexInBlock}`}
                        onClick={onSkipRest}
                        title="Skip the rest timer and move on to this set"
                        className="shrink-0 inline-flex flex-col items-center justify-center min-w-[5.5rem] h-12 px-2 rounded-full border border-amber-500 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors"
                    >
                        <span className="font-mono text-base leading-none" data-testid={`rest-remaining-${indexInBlock}`}>
                            {formatMSS(restRemainingSec)}
                        </span>
                        <span className="text-[8px] tracking-[0.2em] uppercase mt-0.5 inline-flex items-center gap-0.5">
                            <SkipForward className="w-2.5 h-2.5" /> Skip rest
                        </span>
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={`set-complete-${indexInBlock}`}
                        onClick={current ? onComplete : undefined}
                        disabled={!current || disableCheck}
                        title={done ? "Set complete" : current ? "Mark this set complete and move on" : "Not active yet"}
                        className={`shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full border transition-all ${
                            done
                                ? "border-emerald-500 bg-emerald-500/15 text-emerald-400"
                                : current
                                    ? "border-sky-400 bg-sky-500 text-white hover:bg-sky-400 disabled:opacity-50"
                                    : "border-zinc-800 text-zinc-700"
                        }`}
                    >
                        {savingMax ? (
                            <span className="text-[10px] tracking-[0.2em] uppercase">…</span>
                        ) : (
                            <Check className="w-5 h-5" />
                        )}
                    </button>
                )}
            </div>

            {current && setActualWeight && (
                <div className="mt-3 pl-[3.5rem] flex items-center gap-3 flex-wrap">
                    <label
                        htmlFor={`actual-${indexInBlock}`}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-400"
                    >
                        Actual
                    </label>
                    <input
                        id={`actual-${indexInBlock}`}
                        type="number"
                        inputMode="numeric"
                        placeholder={String(prescribed || "—")}
                        value={actualWeight}
                        onChange={(e) => setActualWeight(e.target.value)}
                        data-testid="actual-weight-input"
                        className="w-28 bg-black border border-zinc-800 px-3 py-1.5 text-center text-base text-white focus:outline-none focus:border-sky-500"
                    />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">lbs</span>
                    {pct > 0 && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">· {set.percent_max}% of max</span>
                    )}
                </div>
            )}

            {current && equipment && prescribed > 0 && (
                <div className="mt-3 pl-[3.5rem]">
                    <PlateBreakdown
                        targetWeight={prescribed}
                        barWeight={equipment.weight}
                        equipmentName={equipment.name}
                    />
                </div>
            )}
        </div>
    );
}

const MAX_TYPE_UNITS = {
    weight_lbs: "lbs",
    "1RM": "lbs · 1RM",
    time_secs: "sec",
    speed_mph: "mph",
    resistance: "level",
    height_inches: "in",
};

function TypedMaxInput({ entry, onSave }) {
    const [val, setVal] = useState(entry.value ? String(entry.value) : "");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setVal(entry.value ? String(entry.value) : "");
    }, [entry.value, entry.type]);
    const commit = async () => {
        const num = Number(val);
        if (!Number.isFinite(num) || num <= 0 || num === entry.value) return;
        setSaving(true);
        try {
            await onSave(num);
        } finally {
            setSaving(false);
        }
    };
    const unit = MAX_TYPE_UNITS[entry.type] || entry.type;
    return (
        <div
            data-testid={`typed-max-${entry.type}`}
            className={`inline-flex items-center gap-2 border px-3 py-2 ${
                entry.value > 0 ? "border-zinc-800 bg-black/60" : "border-amber-500 bg-amber-500/10"
            }`}
        >
            <span className={`text-[9px] tracking-[0.25em] uppercase ${entry.value > 0 ? "text-zinc-400" : "text-amber-300"}`}>
                {entry.label || "Max"}
            </span>
            <input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                placeholder="—"
                value={val}
                disabled={saving}
                onChange={(e) => setVal(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                data-testid={`typed-max-input-${entry.type}`}
                className="w-16 bg-transparent text-center font-display text-xl tracking-wider text-white focus:outline-none disabled:opacity-50"
            />
            <span className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">{unit}</span>
        </div>
    );
}
